/**
 * Company Detail Page
 *
 * Visar ett företags detaljer och årsredovisningar
 */

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Building2,
  Plus,
  FileText,
  ArrowLeft,
  Users,
  UserCog,
  HeartPulse,
  Edit,
  ChevronRight,
  Trash2,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Zap,
  ArrowRight,
  Lock,
  Clock,
  Download,
  BookOpen,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  Send,
  ScrollText,
  Sparkles,
} from "lucide-react";
import {
  companiesApi,
  reportsApi,
  subscriptionsApi,
  configApi,
  membersApi,
  protocolsApi,
  type CompanyWithDetails,
  type AnnualReportSummary,
  type AnnualReport,
  type BillingCompany,
  type Protocol,
} from "@/lib/api";
import { ProtocolWizard, ProtocolList } from "@/components/protocols";
import { useQueryClient } from "@tanstack/react-query";
import { useShareBook, shareBookKeys } from "@/hooks/use-share-book";
import { Spinner } from "@/components/ui/spinner";
import { BoardMembersSection } from "@/components/BoardMembersSection";
import { PaymentModal } from "@/components/PaymentModal";
import { EditCompanyModal } from "@/components/EditCompanyModal";
import { MembersSection } from "@/components/company/MembersSection";
import { FinancialHealth } from "@/components/company/FinancialHealth";
import { Recommendations } from "@/components/company/Recommendations";
import { FinalizeReportModal } from "@/components/report/FinalizeReportModal";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";
import { formatCurrency } from "@/lib/format";

interface Tab {
  id: string;
  label: string;
  icon: React.ElementType;
}

const TABS: Tab[] = [
  { id: "rapporter", label: "Rapporter", icon: FileText },
  { id: "protokoll", label: "Protokoll", icon: ScrollText },
  { id: "aktiebok", label: "Aktiebok", icon: BookOpen },
  { id: "styrelse", label: "Styrelse", icon: Users },
  { id: "medlemmar", label: "Medlemmar", icon: UserCog },
  { id: "radgivning", label: "Rådgivning", icon: Lightbulb },
  { id: "halsa", label: "Företagshälsa", icon: HeartPulse },
];

export function CompanyDetail() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [company, setCompany] = useState<CompanyWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Delete report state
  const [reportToDelete, setReportToDelete] = useState<AnnualReportSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Finalize report state
  const [reportToFinalize, setReportToFinalize] = useState<AnnualReportSummary | null>(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Protocol wizard state
  const [showProtocolWizard, setShowProtocolWizard] = useState(false);
  const [protocolToEdit, setProtocolToEdit] = useState<Protocol | null>(null);
  const [protocolVersion, setProtocolVersion] = useState(0); // Used for re-render trigger
  const [actualProtocolCount, setActualProtocolCount] = useState(0);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string | undefined>(undefined);

  // Member count state
  const [memberCount, setMemberCount] = useState(0);

  // Billing state
  const [billingStatus, setBillingStatus] =
    useState<BillingCompany["billingStatus"] | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Edit window config
  const [editWindowDays, setEditWindowDays] = useState(30);

  // Tab state
  const [activeTab, setActiveTab] = useState("rapporter");

  // Financial health data (lazy-loaded)
  const [financialReport, setFinancialReport] = useState<AnnualReport | null>(null);
  const [isLoadingFinancial, setIsLoadingFinancial] = useState(false);
  const [selectedFinancialReportId, setSelectedFinancialReportId] = useState<string | null>(null);

  // Share book data (for aktiebok tab) - check subscription status and existence
  const { data: shareBook, isLoading: isLoadingShareBook, error: shareBookError } = useShareBook(companyId);

  // Check if subscription is required for share book access
  const shareBookErrorMessage = shareBookError instanceof Error ? shareBookError.message : "";
  const _isShareBookSubscriptionRequired = shareBookErrorMessage.includes("aktiverat") || shareBookErrorMessage.includes("prenumeration");
  void _isShareBookSubscriptionRequired; // Kept for future conditional rendering

  // Check if share book already exists (company is activated and has a share book)
  const shareBookExists = !!shareBook && !shareBookError;

  // Check if company has an active subscription (active or canceled but still in period)
  const isCompanyActive = billingStatus === "active_subscription" || billingStatus === "canceled_subscription";

  // Navigate directly to share book if it exists and aktiebok tab is selected
  useEffect(() => {
    if (activeTab === "aktiebok" && shareBookExists && companyId) {
      navigate(`/dashboard/companies/${companyId}/share-book`);
    }
  }, [activeTab, shareBookExists, companyId, navigate]);

  // Handler for "Öppna aktiebok" button click
  const handleOpenShareBook = () => {
    if (!isCompanyActive) {
      // Not subscribed - show payment modal
      setShowPaymentModal(true);
    } else {
      // Subscribed - navigate to share book page
      navigate(`/dashboard/companies/${companyId}/share-book`);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadCompany();
      // Fetch tab counts on initial load
      loadTabCounts(companyId);
    }
    configApi.getPublic().then((res) => {
      if (res.success && res.data) {
        setEditWindowDays(res.data.editWindowDays);
      }
    }).catch(() => {});
  }, [companyId]);

  // Load member and protocol counts for tab badges
  const loadTabCounts = async (id: string) => {
    try {
      const [membersRes, protocolsRes] = await Promise.all([
        membersApi.list(id),
        protocolsApi.list(id),
      ]);
      if (membersRes.success && membersRes.data) {
        setMemberCount(membersRes.data.members.length);
      }
      if (protocolsRes.success && protocolsRes.data) {
        setActualProtocolCount(protocolsRes.data.protocols.length);
      }
    } catch {
      // Silently fail - counts will be updated when tabs are opened
    }
  };

  const loadCompany = async () => {
    if (!companyId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await companiesApi.get(companyId);

      if (response.success && response.data) {
        setCompany(response.data.company);
      } else {
        setError(response.error?.message || "Kunde inte ladda företaget");
      }
    } catch {
      toast.error("Ett fel uppstod vid laddning av företaget");
      setError("Ett fel uppstod vid laddning");
    } finally {
      setIsLoading(false);
    }
  };

  // Check company-level billing status
  const checkBillingStatus = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await subscriptionsApi.getBilling();
      if (response.success && response.data) {
        const match = response.data.companies.find(
          (c) => c.companyId === companyId
        );
        setBillingStatus(match?.billingStatus ?? "inactive");
      }
    } catch {
      // Default to inactive on error
      setBillingStatus("inactive");
    }
  }, [companyId]);

  // Check billing when company loads
  useEffect(() => {
    if (companyId) {
      checkBillingStatus();
    }
  }, [companyId, checkBillingStatus]);

  // Handle payment success redirect from Stripe
  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      const sessionId = searchParams.get("session_id");
      setPaymentSuccess(true);
      setSearchParams({}, { replace: true });

      // Verifiera checkout-sessionen direkt mot Stripe (fungerar utan webhooks)
      const verifyAndRefresh = async () => {
        if (sessionId) {
          try {
            await subscriptionsApi.verifyCheckout(sessionId);
            toast.success("Betalningen är genomförd! Företaget är nu aktiverat.", { id: "payment-success" });
            await checkBillingStatus();
            return;
          } catch {
            // Verify misslyckades — falla tillbaka till polling
          }
        }

        // Fallback: polling om session_id saknas eller verify misslyckades
        toast.success("Betalningen bearbetas...", { id: "payment-success" });
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          await checkBillingStatus();
          if (attempts >= 15) {
            clearInterval(poll);
          }
        }, 2000);
        return () => clearInterval(poll);
      };

      verifyAndRefresh();
    }
  }, [searchParams, setSearchParams, checkBillingStatus]);

  // All reports for the dropdown (sorted by fiscal year, newest first)
  const allFinancialReports = company?.annualReports
    ?.slice()
    .sort((a, b) => b.fiscalYearEnd.localeCompare(a.fiscalYearEnd)) || [];

  // Only completed/exported/finalized reports can be analyzed
  const completedFinancialReports = allFinancialReports
    .filter((r) => r.status === "completed" || r.status === "exported" || r.status === "finalized");

  // Lazy-load financial data when "halsa" or "radgivning" tab is activated
  useEffect(() => {
    if (
      (activeTab === "halsa" || activeTab === "radgivning") &&
      !isLoadingFinancial &&
      completedFinancialReports.length > 0
    ) {
      // Determine which report to load (only from completed reports)
      const targetId =
        selectedFinancialReportId || completedFinancialReports[0]?.id;
      if (!targetId) return;

      // Skip if already loaded
      if (financialReport?.id === targetId) return;

      setIsLoadingFinancial(true);
      reportsApi
        .get(targetId)
        .then((res) => {
          if (res.success && res.data) {
            setFinancialReport(res.data.report);
            if (!selectedFinancialReportId) {
              setSelectedFinancialReportId(targetId);
            }
          }
        })
        .finally(() => setIsLoadingFinancial(false));
    }
  }, [activeTab, selectedFinancialReportId, isLoadingFinancial, completedFinancialReports.length]);

  const handleSelectFinancialReport = (reportId: string) => {
    if (reportId !== selectedFinancialReportId) {
      setSelectedFinancialReportId(reportId);
      setFinancialReport(null); // Clear so useEffect re-fetches
    }
  };

  const handleNewReport = () => {
    navigate(`/dashboard/companies/${companyId}/new-report`);
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await reportsApi.delete(reportToDelete.id);

      if (response.success) {
        // Close modal and refresh company data
        setReportToDelete(null);
        toast.success("Årsredovisningen har raderats");
        await loadCompany();
      } else {
        setDeleteError(response.error?.message || "Kunde inte radera årsredovisningen");
      }
    } catch {
      toast.error("Ett fel uppstod vid radering");
      setDeleteError("Ett fel uppstod vid radering");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirm = (e: React.MouseEvent, report: AnnualReportSummary) => {
    e.preventDefault();
    e.stopPropagation();
    setReportToDelete(report);
    setDeleteError(null);
  };

  const closeDeleteConfirm = () => {
    setReportToDelete(null);
    setDeleteError(null);
  };

  const handleDownloadPdf = async (e: React.MouseEvent, reportId: string) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const result = await reportsApi.downloadPdf(reportId);

      if (result.success && result.blob) {
        const url = window.URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || "arsredovisning.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error(result.error || "Kunde inte ladda ner PDF");
      }
    } catch {
      toast.error("Ett fel uppstod vid nedladdning");
    }
  };

  const handleBoardMembersChange = (members: CompanyWithDetails["boardMembers"]) => {
    if (company) {
      setCompany({ ...company, boardMembers: members });
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "draft":
        return { label: "Utkast", color: "bg-gray-100 text-gray-700" };
      case "in_progress":
        return { label: "Pågående", color: "bg-amber-100 text-amber-700" };
      case "completed":
        return { label: "Klar", color: "bg-green-100 text-green-700" };
      case "finalized":
        return { label: "Skickad", color: "bg-primary-100 text-primary-700" };
      case "exported":
        return { label: "Exporterad", color: "bg-blue-100 text-blue-700" };
      default:
        return { label: status, color: "bg-gray-100 text-gray-700" };
    }
  };

  const getStepLabel = (step: number) => {
    switch (step) {
      case 1:
        return "Bokföringsdata";
      case 2:
        return "Översikt";
      case 3:
        return "Komplettera";
      case 4:
        return "Disposition";
      case 5:
        return "Exportera";
      default:
        return `Steg ${step}`;
    }
  };

  const getEditWindowInfo = (report: AnnualReportSummary): { locked: boolean; remainingDays: number } | null => {
    if (report.status !== "completed" && report.status !== "exported") return null;
    if (editWindowDays <= 0) return null; // Låsning inaktiverad
    // Use completedAt (when "Markera som slutförd" was clicked) as the anchor
    // Fall back to exportedAt for exported reports, then updatedAt for older reports
    const anchor = report.completedAt
      ? new Date(report.completedAt)
      : report.exportedAt
        ? new Date(report.exportedAt)
        : new Date(report.updatedAt);
    const lockDate = new Date(anchor.getTime() + editWindowDays * 24 * 60 * 60 * 1000);
    const remaining = Math.ceil((lockDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    return { locked: remaining <= 0, remainingDays: Math.max(0, remaining) };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || "Företaget hittades inte"}</p>
        <Link to="/dashboard/companies" className="btn btn-primary">
          Tillbaka till företag
        </Link>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title={DASHBOARD_PAGE_META.companyDetail(company.name).title}
        description={DASHBOARD_PAGE_META.companyDetail(company.name).description}
        noIndex
      />
    <div className="space-y-6">
      {/* ═══════════════════════════════════════════════════════════════════
          PREMIUM HEADER SECTION
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 sm:p-8 shadow-2xl">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

        {/* Back link - integrated into hero */}
        <Link
          to="/dashboard/companies"
          className="relative group inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors mb-5"
        >
          <ArrowLeft size={12} className="group-hover:-translate-x-0.5 transition-transform" />
          <span>Tillbaka till företag</span>
        </Link>

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
              <Building2 className="text-white" size={32} />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                <Sparkles className="text-amber-400" size={12} />
                <span className="text-white/80 text-xs font-medium tracking-wide">{company.organizationNumber}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{company.name}</h1>
              {company.seatMunicipality && (
                <p className="text-slate-300 text-sm mt-1">{company.seatMunicipality}</p>
              )}
            </div>
          </div>

          {company.role && company.role !== "viewer" && (
            <button
              onClick={() => setShowEditModal(true)}
              className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-sm px-3 py-2.5 sm:px-5 sm:py-3 border border-white/10 hover:bg-white/15 hover:border-white/20 transition-all duration-300 hover:-translate-y-0.5"
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              <div className="relative flex items-center gap-2 text-white">
                <Edit size={16} />
                <span className="font-medium">Redigera</span>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Billing status banner */}
      {!paymentSuccess && billingStatus === "inactive" && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_60%)]" />
          <div className="relative flex items-center justify-between px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Zap className="text-white" size={22} />
              </div>
              <div>
                <p className="font-semibold text-white text-[15px]">
                  {company.name} har ingen aktiv prenumeration
                </p>
                <p className="text-primary-100 text-sm mt-0.5">
                  Aktivera för att kunna använda alla funktioner.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary-700 font-semibold text-sm rounded-xl hover:bg-primary-50 transition-colors"
            >
              Aktivera
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {billingStatus === "active_subscription" && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-700">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_60%)]" />
          <div className="relative flex items-center px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <CheckCircle className="text-white" size={22} />
              </div>
              <div>
                <p className="font-semibold text-white text-[15px]">
                  Prenumerationen är aktiv
                </p>
                <p className="text-emerald-100 text-sm mt-0.5">
                  Alla funktioner är tillgängliga för {company.name}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {billingStatus === "canceled_subscription" && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_60%)]" />
          <div className="relative flex items-center justify-between px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Clock className="text-white" size={22} />
              </div>
              <div>
                <p className="font-semibold text-white text-[15px]">
                  Prenumerationen avslutas
                </p>
                <p className="text-amber-100 text-sm mt-0.5">
                  Prenumerationen för {company.name} är uppsagd och avslutas vid periodens slut.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-amber-700 font-semibold text-sm rounded-xl hover:bg-amber-50 transition-colors"
            >
              Återaktivera
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {billingStatus === "past_due" && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 to-red-700">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_60%)]" />
          <div className="relative flex items-center justify-between px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <AlertTriangle className="text-white" size={22} />
              </div>
              <div>
                <p className="font-semibold text-white text-[15px]">
                  Betalningen har misslyckats
                </p>
                <p className="text-red-100 text-sm mt-0.5">
                  Uppdatera din betalningsmetod för att behålla åtkomst till alla funktioner.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-semibold text-sm rounded-xl hover:bg-red-50 transition-colors"
            >
              Uppdatera betalning
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Payment success banner */}
      {paymentSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-600" size={20} />
            <p className="font-medium text-green-800">
              Betalningen är genomförd! Företaget är nu aktiverat.
            </p>
          </div>
        </div>
      )}

      {/* Tab bar - horisontellt scrollbar på mobil */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
        <nav
          className="flex gap-1 p-1.5 overflow-x-auto scrollbar-hide"
          aria-label="Tabs"
        >
          {TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            const count =
              tab.id === "rapporter"
                ? company.annualReports?.length || 0
                : tab.id === "styrelse"
                  ? company.boardMembers?.length || 0
                  : tab.id === "medlemmar"
                    ? memberCount
                    : tab.id === "protokoll"
                      ? actualProtocolCount
                      : 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap flex-shrink-0 ${
                  isActive
                    ? "bg-primary-50 text-primary-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {count > 0 && (
                  <span
                    className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? "bg-primary-100 text-primary-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}

      {/* Rapporter-fliken: Årsredovisningar (+ Kvartalsrapporter kommer senare) */}
      {activeTab === "rapporter" && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25 flex-shrink-0">
                <FileText size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900">Årsredovisningar</h2>
                <p className="text-xs text-gray-500">Skapa och hantera årsredovisningar</p>
              </div>
            </div>
            <button
              onClick={handleNewReport}
              className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 px-4 py-2.5 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center justify-center sm:justify-start gap-2 text-white font-medium text-sm sm:text-base">
                <Plus size={18} />
                <span>Ny årsredovisning</span>
              </div>
            </button>
          </div>

          {company.annualReports && company.annualReports.length > 0 ? (
            <div className="space-y-3">
              {company.annualReports.map((report) => {
                const status = getStatusLabel(report.status);
                const fiscalYear = `${report.fiscalYearStart.slice(0, 4)}/${report.fiscalYearEnd.slice(0, 4)}`;
                const editWindow = getEditWindowInfo(report);
                const locked = editWindow?.locked ?? false;

                const highlights = report.financialHighlights;
                const hasFinancialData = highlights && (highlights.netResult !== undefined || highlights.netSales !== undefined);

                return (
                  <Link
                    key={report.id}
                    to={`/dashboard/wizard/${report.id}`}
                    className="block p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <FileText className="text-gray-600" size={20} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Räkenskapsår {fiscalYear}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                              {report.status === "draft"
                                ? `Steg ${report.currentStep}/${5}`
                                : status.label}
                            </span>
                            {report.status === "draft" && (
                              <span className="text-xs text-gray-500">
                                {getStepLabel(report.currentStep)}
                              </span>
                            )}
                          </div>
                          {editWindow && !locked && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <Clock size={11} />
                              Kan redigeras i {editWindow.remainingDays} {editWindow.remainingDays === 1 ? "dag" : "dagar"} till
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        {(locked || report.status === "finalized") && (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500"
                            title={report.status === "finalized"
                              ? "Rapporten har skickats till Bolagsverket och kan inte längre ändras."
                              : "Redigeringsfönstret har passerat. Rapporten kan inte längre ändras."}
                          >
                            <Lock size={12} />
                            {report.status === "finalized" ? "Skickad" : "Låst"}
                          </span>
                        )}
                        {report.status === "completed" &&
                          billingStatus && billingStatus !== "inactive" && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setReportToFinalize(report);
                            }}
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                            title="Lås och skicka till Bolagsverket"
                          >
                            <Send size={12} />
                            Skicka till Bolagsverket
                          </button>
                        )}
                        {(report.status === "completed" || report.status === "exported" || report.status === "finalized") &&
                          billingStatus && billingStatus !== "inactive" && (
                          <button
                            onClick={(e) => handleDownloadPdf(e, report.id)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                            title="Ladda ner PDF"
                          >
                            <Download size={18} />
                          </button>
                        )}
                        {!locked && report.status !== "finalized" && (
                          <button
                            onClick={(e) => openDeleteConfirm(e, report)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Radera årsredovisning"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                        <ChevronRight className="text-gray-400" size={20} />
                      </div>
                    </div>

                    {/* Financial highlights */}
                    {hasFinancialData && (
                      <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {highlights.netResult !== undefined && (
                          <div className="flex items-center gap-2">
                            {highlights.netResult >= 0 ? (
                              <TrendingUp size={14} className="text-green-500" />
                            ) : (
                              <TrendingDown size={14} className="text-red-500" />
                            )}
                            <div>
                              <p className="text-xs text-gray-500">Årets resultat</p>
                              <p className={`text-sm font-medium ${highlights.netResult >= 0 ? "text-green-700" : "text-red-700"}`}>
                                {formatCurrency(highlights.netResult)}
                              </p>
                            </div>
                          </div>
                        )}
                        {highlights.netSales !== undefined && highlights.netSales !== 0 && (
                          <div>
                            <p className="text-xs text-gray-500">Nettoomsättning</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(highlights.netSales)}
                            </p>
                          </div>
                        )}
                        {highlights.totalAssets !== undefined && highlights.totalAssets !== 0 && (
                          <div>
                            <p className="text-xs text-gray-500">Balansomslutning</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(highlights.totalAssets)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-600 mb-4">
                Inga årsredovisningar ännu. Skapa din första!
              </p>
              <button
                onClick={handleNewReport}
                className="btn btn-primary"
              >
                <Plus size={20} className="mr-2" />
                Skapa årsredovisning
              </button>
            </div>
          )}
        </div>
      )}

      {/* Protokoll tab */}
      {activeTab === "protokoll" && companyId && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25 flex-shrink-0">
                <ScrollText className="text-white" size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900">Protokoll</h2>
                <p className="text-xs text-gray-500">
                  Dagordningar och mötesprotokoll enligt ABL 7 kap
                </p>
              </div>
            </div>
          </div>

          <ProtocolList
            key={`protocol-list-${protocolVersion}`}
            companyId={companyId}
            onCreateNew={(agendaId?: string) => {
              if (!isCompanyActive) {
                setShowPaymentModal(true);
              } else {
                setSelectedAgendaId(agendaId);
                setShowProtocolWizard(true);
              }
            }}
            onEdit={(protocol) => {
              setProtocolToEdit(protocol);
              setShowProtocolWizard(true);
            }}
            onCountChange={setActualProtocolCount}
          />
        </div>
      )}

      {activeTab === "aktiebok" && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
          {/* Show loading while checking subscription status */}
          {isLoadingShareBook ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600 mr-3" size={24} />
              <span className="text-gray-600">Laddar...</span>
            </div>
          ) : (
            /* Always show intro view - button handles navigation/payment logic */
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 flex-shrink-0">
                    <BookOpen className="text-white" size={20} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900">Digital aktiebok</h2>
                    <p className="text-xs text-gray-500">
                      Hantera aktieägare, aktieslag och transaktioner enligt ABL 5 kap
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleOpenShareBook}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 px-4 py-2.5 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex items-center justify-center sm:justify-start gap-2 text-white font-medium text-sm sm:text-base">
                    <span>Öppna aktiebok</span>
                    <ChevronRight size={16} />
                  </div>
                </button>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl p-5">
                <h3 className="font-semibold text-blue-900 mb-3">Vad är den digitala aktieboken?</h3>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    Fullständig spårbarhet av alla ägarförändringar
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    Stöd för flera aktieslag (A, B, preferens, etc.)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    Automatisk nummerserier för aktier
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    Händelselogg med SHA-256 checksummor för ABL-compliance
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    Export till PDF och Excel
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "radgivning" && (
        <Recommendations
          report={financialReport}
          company={company}
          isLoading={isLoadingFinancial}
          availableReports={allFinancialReports}
          selectedReportId={selectedFinancialReportId || undefined}
          onSelectReport={handleSelectFinancialReport}
        />
      )}

      {activeTab === "halsa" && (
        <FinancialHealth
          report={financialReport}
          company={company}
          isLoading={isLoadingFinancial}
          availableReports={allFinancialReports}
          selectedReportId={selectedFinancialReportId || undefined}
          onSelectReport={handleSelectFinancialReport}
        />
      )}

      {activeTab === "styrelse" && (
        <BoardMembersSection
          companyId={company.id}
          members={company.boardMembers || []}
          onMembersChange={handleBoardMembersChange}
          editable
        />
      )}

      {activeTab === "medlemmar" && (
        <MembersSection
          companyId={company.id}
          userRole={company.role || "viewer"}
          onCountChange={setMemberCount}
        />
      )}

      {/* Edit Company Modal */}
      {showEditModal && (
        <EditCompanyModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          company={{
            id: company.id,
            name: company.name,
            organizationNumber: company.organizationNumber,
            type: company.type,
            seatMunicipality: company.seatMunicipality,
            address: company.address,
            postalCode: company.postalCode,
            city: company.city,
          }}
          onUpdated={(updatedCompany) => {
            setCompany({ ...company, ...updatedCompany });
          }}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && company && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          companyName={company.name}
          companyId={company.id}
          onActivated={() => {
            setBillingStatus("active_subscription");
            setShowPaymentModal(false);
            // Invalidate share book query to refresh subscription status
            if (companyId) {
              queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
            }
          }}
        />
      )}

      {/* Delete Report Confirmation Modal */}
      {reportToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Radera årsredovisning?
                </h3>
                <p className="text-gray-600 mb-1">
                  Du håller på att radera årsredovisningen för räkenskapsår{" "}
                  <strong>
                    {reportToDelete.fiscalYearStart.slice(0, 4)}/
                    {reportToDelete.fiscalYearEnd.slice(0, 4)}
                  </strong>.
                </p>
                <p className="text-sm text-gray-500">
                  All data inklusive SIE-import och kompletteringar kommer att tas bort permanent.
                </p>

                {deleteError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{deleteError}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={closeDeleteConfirm}
                disabled={isDeleting}
                className="flex-1 btn btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteReport}
                disabled={isDeleting}
                className="flex-1 btn bg-red-600 text-white hover:bg-red-700 flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Raderar...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} className="mr-2" />
                    Radera
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Report Modal */}
      {reportToFinalize && (
        <FinalizeReportModal
          isOpen={!!reportToFinalize}
          onClose={() => setReportToFinalize(null)}
          report={reportToFinalize}
          companyName={company.name}
          onFinalized={() => {
            setReportToFinalize(null);
            loadCompany();
          }}
        />
      )}

      {/* Protocol Wizard Modal */}
      {showProtocolWizard && companyId && (
        <ProtocolWizard
          companyId={companyId}
          isOpen={showProtocolWizard}
          existingProtocol={protocolToEdit || undefined}
          agendaId={selectedAgendaId}
          onClose={() => {
            setShowProtocolWizard(false);
            setProtocolToEdit(null);
            setSelectedAgendaId(undefined);
          }}
          onSuccess={() => {
            setShowProtocolWizard(false);
            setProtocolToEdit(null);
            setSelectedAgendaId(undefined);
            // Trigger a re-render of the ProtocolList by incrementing the version
            setProtocolVersion((prev) => prev + 1);
          }}
        />
      )}
    </div>
    </>
  );
}
